/** Analysis, as opposed to counting.
 *
 *  Reports answers questions somebody chose in advance and answers them backwards:
 *  how many, by whom, what rate, over a range that has already happened. This page
 *  answers the two it could not - what is about to go wrong, and why the numbers look
 *  the way they do.
 *
 *  Every section says what it answers above the figures. A report nobody can state
 *  the purpose of gets read once and then ignored, and eight of them would be eight
 *  times that.
 *
 *  **What may slip is loaded separately and on purpose.** It is a snapshot of now and
 *  everything else is scoped to the range, so it sits above the range picker rather
 *  than under it - the workload rail's own docstring explains why mixing the two is
 *  how a report gets read wrong.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import { Banner, ErrorNote, Spinner, Tabs, Tag, fmtDate, monthStartISO, todayISO }
  from '@/components/ui'

type AtRisk = {
  as_of: string; total: number; overdue: number; due_within_2: number
  rows: {
    ref: number; task: string; company: string; responsible: string
    priority: string; date: string; days_left: number; carried: number
    score: number; why: string[]
  }[]
}
type Analysis = {
  range: { from: string; to: string }
  clients: { rows: {
    company: string; total: number; completed: number; rate: number
    rework: number; rework_rate: number; carried: number; open_now: number
    median_cycle_days: number; oldest_open_days: number
  }[] }
  cycle_time: {
    median_days: number; completed: number; priority_verdict: string
    shared_note: string
    by_priority: { priority: string; completed: number; median_days: number }[]
    by_person: { name: string; completed: number; median_days: number }[]
  }
  rejections: {
    total: number; tasks_affected: number
    by_approver: { name: string; rejections: number }[]
    by_responsible: { name: string; rejections: number }[]
    repeatedly_rejected: { ref: number; task: string; company: string
                           responsible: string; times: number }[]
    reasons: { ref: number; company: string; by: string; reason: string }[]
  }
  fairness: {
    rows: { name: string; assigned: number }[]
    median: number; busiest: number; quietest: number; spread: number
    verdict: string
  }
  recurring: {
    rules: number; invalid: number; with_gaps: number
    rows: { ref: string; task: string; company: string; active: boolean
            valid: boolean; problems: string[]; expected: number | null
            generated: number; completed: number; carried: number
            rate: number; missing: number | null }[]
  }
  managers: {
    note: string
    rows: { name: string; team_size: number; assigned: number; completed: number
            rate: number; open_now: number; overdue: number; carried: number }[]
  }
  day_close: {
    working_days: number; closed: number; left_open: number; reopened: number
    median_carried: number; verdict: string
    rows: { date: string; closed: boolean; reopened: boolean; total: number
            completed: number; carried: number }[]
  }
}

/** One line per analysis: what it found, and whether that is worrying.
 *
 *  This is what the long scroll was actually good for - noticing something you were
 *  not looking for - so tabs without it would be a straight loss. Three of these
 *  sentences come from the backend, which already words its own verdicts; the rest are
 *  derived here, because they are presentation and not new analysis.
 */
type Finding = { tab: Tab; label: string; tone: 'warn' | 'ok'; text: string }

function findings(risk: AtRisk | undefined, a: Analysis | undefined): Finding[] {
  const out: Finding[] = []
  if (risk) {
    out.push(risk.total === 0
      ? { tab: 'risk', label: 'What may slip', tone: 'ok',
          text: 'Nothing open looks like slipping.' }
      : { tab: 'risk', label: 'What may slip', tone: risk.overdue ? 'warn' : 'ok',
          text: `${risk.total} open task${risk.total === 1 ? '' : 's'} worth a look`
                + `${risk.overdue ? `, ${risk.overdue} already late` : ''}.` })
  }
  if (!a) return out

  const bad = a.clients.rows.filter((r) => r.rework_rate >= 20 || r.oldest_open_days >= 14)
  out.push(bad.length
    ? { tab: 'clients', label: 'Clients', tone: 'warn',
        text: `${bad.length} client${bad.length === 1 ? '' : 's'} with high rework or `
              + `something sitting a fortnight: ${bad.slice(0, 3).map((r) => r.company).join(', ')}.` }
    : { tab: 'clients', label: 'Clients', tone: 'ok',
        text: 'No client stands out for rework or age.' })

  out.push(a.cycle_time.priority_verdict
    ? { tab: 'cycle', label: 'Cycle time', tone:
          a.cycle_time.priority_verdict.includes('intended') ? 'ok' : 'warn',
        text: a.cycle_time.priority_verdict }
    : { tab: 'cycle', label: 'Cycle time', tone: 'ok',
        text: `Median ${a.cycle_time.median_days} day(s) from raised to done.` })

  const rej = a.rejections
  out.push(rej.total === 0
    ? { tab: 'rework', label: 'Rework', tone: 'ok', text: 'Nothing came back.' }
    : { tab: 'rework', label: 'Rework', tone:
          rej.repeatedly_rejected.length ? 'warn' : 'ok',
        text: `${rej.total} rejection${rej.total === 1 ? '' : 's'} across `
              + `${rej.tasks_affected} task${rej.tasks_affected === 1 ? '' : 's'}`
              + `${rej.repeatedly_rejected.length
                   ? `, ${rej.repeatedly_rejected.length} more than once` : ''}.` })

  out.push(a.fairness.verdict
    ? { tab: 'fairness', label: 'Workload', tone:
          a.fairness.verdict.includes('times the median') ? 'warn' : 'ok',
        text: a.fairness.verdict }
    : { tab: 'fairness', label: 'Workload', tone: 'ok',
        text: 'The work is spread evenly enough to say nothing about.' })

  const rec = a.recurring
  const rough = rec.invalid + rec.with_gaps
  out.push(rough
    ? { tab: 'recurring', label: 'Recurring', tone: 'warn',
        text: `${rec.invalid} rule${rec.invalid === 1 ? '' : 's'} invalid and `
              + `${rec.with_gaps} producing less than expected.` }
    : { tab: 'recurring', label: 'Recurring', tone: 'ok',
        text: `All ${rec.rules} standing rule${rec.rules === 1 ? '' : 's'} pulling `
              + 'their weight.' })

  const behind = a.managers.rows.filter((r) => r.overdue > 0)
  out.push(behind.length
    ? { tab: 'managers', label: 'Managers', tone: 'warn',
        text: `${behind.length} team${behind.length === 1 ? '' : 's'} carrying `
              + 'overdue work.' }
    : { tab: 'managers', label: 'Managers', tone: 'ok',
        text: 'No team is carrying overdue work.' })

  out.push(a.day_close.left_open
    ? { tab: 'dayclose', label: 'Day close', tone: 'warn',
        text: a.day_close.verdict
              || `${a.day_close.left_open} working day(s) never closed.` }
    : { tab: 'dayclose', label: 'Day close', tone: 'ok',
        text: `All ${a.day_close.closed} working day(s) closed.` })

  return out
}

function Overview({ rows, onPick }: {
  rows: Finding[]; onPick: (t: Tab) => void
}) {
  if (!rows.length) return null
  // Worrying first: the point of a summary is that the thing needing attention is
  // not below the fold.
  const sorted = [...rows].sort((x, y) => (x.tone === 'warn' ? 0 : 1)
                                        - (y.tone === 'warn' ? 0 : 1))
  return (
    <div className="card mt-4">
      <div className="card-head"><b className="text-[13px]">What the numbers say</b></div>
      <ul>
        {sorted.map((f) => (
          <li key={f.tab} className="border-b border-rule-2 last:border-0">
            <button type="button" onClick={() => onPick(f.tab)}
                    className="w-full text-left px-4 py-2.5 hover:bg-paper
                               flex items-start gap-2.5">
              <span aria-hidden className={`mt-[3px] shrink-0 text-[12px] ${
                f.tone === 'warn' ? 'text-pend-ink' : 'text-done-ink'}`}>
                {f.tone === 'warn' ? '⚠' : '✓'}
              </span>
              <span className="min-w-0">
                <span className="text-[12.8px]">{f.text}</span>
                <span className="block text-[11.5px] text-ink-3 mt-0.5">
                  {f.label} →
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A section, with the question it answers stated above the figures. */
function Section({ title, answers, children, empty }: {
  title: string; answers: string; children?: React.ReactNode; empty?: string
}) {
  return (
    <div className="card mt-4">
      <div className="card-head">
        <b className="text-[13px]">{title}</b>
      </div>
      <div className="p-4">
        <p className="text-[11.8px] text-ink-3 mb-3 max-w-3xl leading-relaxed">
          {answers}
        </p>
        {empty ? <p className="text-[12.5px] text-ink-3">{empty}</p> : children}
      </div>
    </div>
  )
}

function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="table-wrap">
      <table className="w-full border-collapse">
        <thead><tr>{head.map((h) => (
          <th key={h} className="th text-left">{h}</th>))}</tr></thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i}>{cells.map((c, j) => (
              <td key={j} className="td text-[12.5px]">{c}</td>))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The tabs, in the order they are worth reading: what is about to go wrong first,
 *  then the diagnostics, then the housekeeping. */
const TABS = [
  ['overview', 'Overview'],
  ['risk', 'What may slip'],
  ['clients', 'Clients'],
  ['cycle', 'Cycle time'],
  ['rework', 'Rework'],
  ['fairness', 'Workload'],
  ['recurring', 'Recurring'],
  ['managers', 'Managers'],
  ['dayclose', 'Day close'],
] as const
type Tab = typeof TABS[number][0]

/** Which tabs are scoped to the date range. `risk` is a snapshot of now, and
 *  `overview` reads both - so the picker is shown for everything except the one tab
 *  the range does not touch, and its absence there is the point. */
const RANGED: Tab[] = ['overview', 'clients', 'cycle', 'rework', 'fairness',
                       'recurring', 'managers', 'dayclose']

export function Insights() {
  const [from, setFrom] = useState(monthStartISO())
  const [to, setTo] = useState(todayISO())
  const [tab, setTab] = useState<Tab>('overview')

  const risk = useQuery({
    queryKey: ['at-risk'],
    queryFn: () => api.get<AtRisk>('/reports/at-risk'),
  })
  const a = useQuery({
    queryKey: ['analysis', from, to],
    queryFn: () => api.get<Analysis>(
      `/reports/analysis?date_from=${from}&date_to=${to}`),
  })

  return (
    <>
      <h2 className="text-[17px] font-semibold tracking-tight mb-1">Insights</h2>
      <p className="text-[12.5px] text-ink-3 mb-4 max-w-3xl leading-relaxed">
        Reports counts what happened. This looks for what is about to, and for the
        reason behind a figure rather than the figure.
      </p>

      {/* Tabs rather than one long scroll - eight analyses stacked vertically meant
          the last of them was rarely reached. The Overview is first and exists to
          keep what the scroll was good for: noticing something you were not looking
          for. Every line on it links to the tab it came from. */}
      <Tabs tabs={TABS} active={tab} onPick={setTab} />

      {/* A snapshot of right now, not of the range - which is why this tab is the
          one with no date picker. */}
      {(tab === 'risk' || tab === 'overview') && (
      risk.isLoading ? <Spinner label="Looking for what may slip" />
        : risk.error ? <ErrorNote error={risk.error} />
        : risk.data ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5">
              {[
                ['At risk now', String(risk.data.total), 'open work worth a look'],
                ['Already late', String(risk.data.overdue), 'past its own date'],
                ['Due within 2 days', String(risk.data.due_within_2), 'and not done'],
              ].map(([l, v, s]) => (
                <div key={l} className="card p-4">
                  <div className="label">{l}</div>
                  <div className="mono text-3xl font-semibold tracking-tight">{v}</div>
                  <div className="text-[11.8px] text-ink-3 mt-1">{s}</div>
                </div>
              ))}
            </div>

            {tab === 'risk' && (
            <Section
              title="What may slip"
              answers={`Open work as it stands on ${fmtDate(risk.data.as_of)}, worst
                        first. Not scoped to the range below - this is a snapshot of
                        now, which is the only way it can be useful. Every row says
                        why it is here so the ordering can be argued with.`}
              empty={risk.data.rows.length === 0
                ? 'Nothing open is late, due within two days, or has been carried before.'
                : undefined}>
              <Table
                head={['Ref', 'Task', 'Company', 'Responsible', 'Due', 'Why']}
                rows={risk.data.rows.map((r) => [
                  <span className="mono text-primary">{r.ref}</span>,
                  r.task,
                  r.company,
                  r.responsible,
                  <span className={r.days_left < 0 ? 'text-rej-ink font-semibold' : ''}>
                    {fmtDate(r.date)}
                  </span>,
                  r.why.join(' · '),
                ])} />
            </Section>
            )}
          </>
        ) : null)}

      {RANGED.includes(tab) && (
      <div className="card mt-4 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="in-from">Everything below, from</label>
          <input id="in-from" type="date" className="field w-44" value={from}
                 onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="in-to">to</label>
          <input id="in-to" type="date" className="field w-44" value={to}
                 onChange={(e) => setTo(e.target.value)} />
        </div>
        <span className="flex-1" />
        <Link className="btn btn-sm" to="/explore">Filter &amp; export the rows →</Link>
      </div>
      )}

      {/* After the picker, not before it. The picker says "everything below", and
          these findings are range-scoped - putting them above it made the label a
          lie about the one thing on screen. */}
      {tab === 'overview' && (
        a.isLoading ? <Spinner label="Working it out" />
        : a.error ? <ErrorNote error={a.error} />
        : <Overview rows={findings(risk.data, a.data)} onPick={setTab} />
      )}

      {tab === 'overview' || tab === 'risk' ? null
        : a.isLoading ? <Spinner label="Working it out" />
        : a.error ? <ErrorNote error={a.error} />
        : !a.data ? null : (
        <>
          {tab === 'clients' && (
          <Section
            title="Which clients are being let down"
            answers="Completion rate is only half the story - a client whose work
                     always finishes but takes three weeks and comes back twice is
                     not being served well. Worst first."
            empty={a.data.clients.rows.length === 0
              ? 'No tasks in this range.' : undefined}>
            <Table
              head={['Company', 'Tasks', 'Done', 'Rate', 'Median days',
                     'Came back', 'Carried', 'Oldest open']}
              rows={a.data.clients.rows.map((r) => [
                r.company, r.total, r.completed,
                <span className={r.rate < 60 ? 'text-rej-ink font-semibold' : ''}>
                  {r.rate}%
                </span>,
                r.median_cycle_days || '—',
                r.rework ? `${r.rework} (${r.rework_rate}%)` : '—',
                r.carried || '—',
                r.oldest_open_days ? `${r.oldest_open_days} days` : '—',
              ])} />
          </Section>
          )}

          {tab === 'cycle' && (
          <Section
            title="How long work actually takes"
            answers="Raised to done, which is what a client experiences - approval
                     turnaround is one leg of that journey. A median rather than an
                     average, so one task that took a year does not move the figure
                     the firm reads.">
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="label">Median, all completed work</div>
                <div className="mono text-3xl font-semibold tracking-tight">
                  {a.data.cycle_time.median_days || '—'}
                </div>
                <div className="text-[11.8px] text-ink-3">
                  days, over {a.data.cycle_time.completed} task(s)
                </div>
              </div>
              {a.data.cycle_time.by_priority.length > 0 && (
                <div className="flex-1 min-w-64">
                  <div className="label mb-1">By priority</div>
                  <Table head={['Priority', 'Done', 'Median days']}
                         rows={a.data.cycle_time.by_priority.map((p) => [
                           p.priority, p.completed, p.median_days])} />
                </div>
              )}
            </div>
            {a.data.cycle_time.priority_verdict && (
              <div className="mt-3">
                {/* `info` when the ladder holds, `warn` when it does not - the
                    two readings are a reassurance and a finding, and they should not
                    look the same. */}
                <Banner tone={a.data.cycle_time.priority_verdict.includes('intended')
                               ? 'info' : 'warn'}>
                  {a.data.cycle_time.priority_verdict}
                </Banner>
              </div>
            )}
            {a.data.cycle_time.by_person.length > 0 && (
              <div className="mt-4">
                <div className="label mb-1">By person — slowest first</div>
                <Table head={['Person', 'Done', 'Median days']}
                       rows={a.data.cycle_time.by_person.map((p) => [
                         p.name, p.completed, p.median_days])} />
                <p className="text-[11.5px] text-ink-3 mt-1">
                  {a.data.cycle_time.shared_note}
                </p>
              </div>
            )}
          </Section>
          )}

          {tab === 'rework' && (
          <Section
            title="Work that came back"
            answers="A rework rate says how often; this says who sent it back, whose
                     work it was, and what they wrote. The rejection reason reaches
                     the person verbatim and is never gathered up anywhere else."
            empty={a.data.rejections.total === 0
              ? 'Nothing was rejected in this range.' : undefined}>
            <div className="flex flex-wrap gap-8">
              <div className="min-w-56">
                <div className="label mb-1">Sent back by</div>
                <Table head={['Approver', 'Times']}
                       rows={a.data.rejections.by_approver.map((r) => [
                         r.name, r.rejections])} />
              </div>
              <div className="min-w-56">
                <div className="label mb-1">Work came back to</div>
                <Table head={['Person', 'Times']}
                       rows={a.data.rejections.by_responsible.map((r) => [
                         r.name, r.rejections])} />
              </div>
            </div>
            {a.data.rejections.repeatedly_rejected.length > 0 && (
              <div className="mt-4">
                <div className="label mb-1">Rejected more than once</div>
                <Table head={['Ref', 'Task', 'Company', 'Responsible', 'Times']}
                       rows={a.data.rejections.repeatedly_rejected.map((r) => [
                         <span className="mono text-primary">{r.ref}</span>,
                         r.task, r.company, r.responsible, r.times])} />
              </div>
            )}
            {a.data.rejections.reasons.length > 0 && (
              <div className="mt-4">
                <div className="label mb-1">What was said</div>
                <Table head={['Ref', 'Company', 'By', 'Reason']}
                       rows={a.data.rejections.reasons.map((r) => [
                         <span className="mono text-primary">{r.ref}</span>,
                         r.company, r.by, r.reason])} />
              </div>
            )}
          </Section>
          )}

          {tab === 'fairness' && (
          <Section
            title="Is the work shared"
            answers="The Team screen answers who has room today. This answers whether
                     the work has been spread over the range, which is a different
                     question - a firm can look balanced every afternoon and still
                     have given one person twice the year."
            empty={a.data.fairness.rows.length === 0
              ? 'Nobody was assigned anything in this range.' : undefined}>
            <div className="flex flex-wrap gap-8 items-start">
              <div>
                <div className="label">Busiest vs median</div>
                <div className="mono text-3xl font-semibold tracking-tight">
                  {a.data.fairness.spread || '—'}×
                </div>
                <div className="text-[11.8px] text-ink-3">
                  {a.data.fairness.busiest} against a median of {a.data.fairness.median}
                </div>
              </div>
              <div className="flex-1 min-w-64">
                <Table head={['Person', 'Assigned']}
                       rows={a.data.fairness.rows.map((r) => [
                         r.name, r.assigned])} />
              </div>
            </div>
            {a.data.fairness.verdict && (
              <div className="mt-3"><Banner tone="warn">
                {a.data.fairness.verdict}
              </Banner></div>
            )}
          </Section>
          )}

          {tab === 'recurring' && (
          <Section
            title="Are the standing rules pulling their weight"
            answers="What each recurring rule should have produced in this range
                     against what it did. A rule generating nothing is invisible
                     everywhere else - the tasks simply are not there, and an absence
                     is the one thing no list shows you."
            empty={a.data.recurring.rows.length === 0
              ? 'No recurring rules yet.' : undefined}>
            {(a.data.recurring.invalid > 0 || a.data.recurring.with_gaps > 0) && (
              <div className="mb-3"><Banner tone="warn">
                {a.data.recurring.with_gaps > 0 && (
                  <>{a.data.recurring.with_gaps} rule(s) produced less than the
                     schedule says. </>)}
                {a.data.recurring.invalid > 0 && (
                  <>{a.data.recurring.invalid} rule(s) cannot run at all.</>)}
              </Banner></div>
            )}
            <Table
              head={['Rule', 'Task', 'Company', 'Expected', 'Made', 'Missing',
                     'Done', 'Rate']}
              rows={a.data.recurring.rows.map((r) => [
                <span className="mono text-primary">{r.ref}</span>,
                r.task, r.company,
                r.expected === null
                  ? <Tag tone="bad">cannot run</Tag> : r.expected,
                r.generated,
                r.missing
                  ? <span className="text-rej-ink font-semibold">{r.missing}</span>
                  : '—',
                r.completed,
                r.generated ? `${r.rate}%` : '—',
              ])} />
          </Section>
          )}

          {tab === 'managers' && (
          <Section
            title="Each manager's team"
            answers="Every other per-person figure in the app is flat, so a manager
                     with four reports adds four rows up by hand and gets no answer at
                     all about a report's own reports. This rolls the whole subtree up,
                     at any depth."
            empty={a.data.managers.rows.length === 0
              ? 'Nobody has anybody reporting to them yet.' : undefined}>
            <Table
              head={['Manager', 'Team', 'Assigned', 'Done', 'Rate', 'Open now',
                     'Overdue', 'Carried']}
              rows={a.data.managers.rows.map((r) => [
                r.name, r.team_size, r.assigned, r.completed,
                <span className={r.rate < 60 ? 'text-rej-ink font-semibold' : ''}>
                  {r.rate}%
                </span>,
                r.open_now,
                r.overdue
                  ? <span className="text-rej-ink font-semibold">{r.overdue}</span>
                  : '—',
                r.carried || '—',
              ])} />
            <p className="text-[11.5px] text-ink-3 mt-1">{a.data.managers.note}</p>
          </Section>
          )}

          {tab === 'dayclose' && (
          <Section
            title="Did the day get closed"
            answers="Carry-forward is what moves unfinished work, so a working day
                     nobody closed is a day whose work silently stayed put. This has
                     been recorded since the feature existed and nothing read it back."
            empty={a.data.day_close.working_days === 0
              ? 'No working days in this range.' : undefined}>
            {a.data.day_close.verdict && (
              <div className="mb-3"><Banner tone="warn">
                {a.data.day_close.verdict}
              </Banner></div>
            )}
            <div className="flex flex-wrap gap-8 mb-3">
              {[
                ['Working days', String(a.data.day_close.working_days)],
                ['Closed', String(a.data.day_close.closed)],
                ['Left open', String(a.data.day_close.left_open)],
                ['Reopened', String(a.data.day_close.reopened)],
                ['Median moved', String(a.data.day_close.median_carried)],
              ].map(([l, v]) => (
                <div key={l}>
                  <div className="label">{l}</div>
                  <div className="mono text-xl font-semibold">{v}</div>
                </div>
              ))}
            </div>
            <Table
              head={['Date', 'Closed', 'Tasks', 'Done', 'Moved']}
              rows={a.data.day_close.rows.map((r) => [
                fmtDate(r.date),
                r.closed
                  ? <Tag>closed{r.reopened ? ' · reopened' : ''}</Tag>
                  : <Tag tone="bad">never closed</Tag>,
                r.total || '—', r.completed || '—', r.carried || '—',
              ])} />
          </Section>
          )}
        </>
      )}
    </>
  )
}
